import { api } from './klient';

// Typene speiler API-et for kunden (docs/beslutninger/0019). Tekstene kommer på deltakerens språk.

export type Sprak = 'nb' | 'sv' | 'en';

export interface Lenkestatus {
  status: 'krever_kode' | 'gyldig';
  kundenavn: string;
  sprak: Sprak;
}

export interface Alternativ {
  verdi: string;
  tekst: string;
  anbefalt: boolean;
}

export interface Valg {
  verdi: string;
  tekst: string;
}

export interface Kolonne {
  id: string;
  type: string;
  tekst: string;
  alternativer: Valg[];
}

export type Svarverdi = string | number | string[] | null | Record<string, unknown> | Record<string, unknown>[];

export interface GittSvar {
  verdi: Svarverdi;
  kundensOrd: string | null;
  status: 'gjeldende' | 'bekreft_pa_nytt' | 'gjelder_ikke_lenger';
  vetIkke: boolean;
  av: string;
  tidspunkt: string;
}

export interface Sporsmal {
  id: string;
  instans: string;
  modulId: string;
  modulTittel: string;
  type: string;
  tekst: string;
  hvorfor: string;
  alternativer: Alternativ[];
  annet: boolean;
  vetIkke: boolean;
  kritisk: boolean;
  min: number | null;
  max: number | null;
  enhet: string | null;
  kolonner: Kolonne[];
  egenskap: string | null;
  filtype: string | null;
  forhandsutfyll: string | null;
  forelder: { id: string; tekst: string; profil: string } | null;
  svar: GittSvar | null;
}

export interface Neste {
  ferdig: boolean;
  sporsmal: Sporsmal | null;
}

export interface ModulFremdrift {
  modulId: string;
  tittel: string;
  besvart: number;
  synlige: number;
  ferdig: boolean;
  /** Omtrent hvor mange minutter som gjenstår i modulen (T110). */
  minutter?: number;
}

export interface Fremdrift {
  moduler: ModulFremdrift[];
  besvart: number;
  synlige: number;
  ferdig: boolean;
  /** Omtrent hvor mange minutter som gjenstår i hele reisen (T110). */
  minutter?: number;
}

export interface SvarInn {
  sporsmalId: string;
  instans: string;
  verdi?: Svarverdi;
  kundensOrd?: string | null;
  vetIkke?: boolean;
  hoppOver?: boolean;
}

export interface SvarResultat {
  neste: Neste;
  konsekvenser: { sporsmalId: string; instans: string; status: string }[];
}

/** Et svar som gjelder, med tekstene som trengs for å vise det. */
export interface GjeldendeSvar {
  sporsmalId: string;
  instans: string;
  modulId: string;
  modulTittel: string;
  type: string;
  tekst: string;
  alternativer: Valg[];
  profil: string | null;
  verdi: Svarverdi;
  kundensOrd: string | null;
  status: 'gjeldende' | 'bekreft_pa_nytt';
  vetIkke: boolean;
  av: string;
  tidspunkt: string;
}

/** Den innloggede deltakeren. `moduler` er tom når deltakeren har tilgang til alle. */
export interface Meg {
  id: string;
  navn: string;
  epost: string;
  erEier: boolean;
  sprak: Sprak;
  kundenavn: string;
  moduler: string[];
}

export interface Deltaker {
  id: string;
  navn: string;
  epost: string;
  rolle: string | null;
  moduler: string[];
  erEier: boolean;
  sistAktiv: string | null;
}

export interface NyKollega {
  navn: string;
  epost: string;
  rolle?: string;
  moduler: string[];
  sprak: Sprak;
}

export interface SammendragSvar {
  sporsmalId: string;
  instans: string;
  beslutningId: string;
  type: string;
  sporsmal: string;
  profil: string | null;
  valgte: Valg[];
  verdi: Svarverdi;
  kundensOrd: string | null;
  sikkerhet: string;
  kilde: string;
  status: 'gjeldende' | 'bekreft_pa_nytt';
  vetIkke: boolean;
  av: string;
  tidspunkt: string;
}

export interface SammendragModul {
  id: string;
  tittel: string;
  besvart: number;
  synlige: number;
  svar: SammendragSvar[];
  flagg: { type: string; sporsmalId: string | null; sporsmal: string | null }[];
  /** Sammendraget av modulen skrevet av oppsummereren, når det gjelder for svarene nå. */
  oppsummering?: string | null;
}

export interface Sammendrag {
  reiseId: string;
  kundenavn: string;
  sprak: Sprak;
  innholdVersjon: string;
  moduler: SammendragModul[];
  ubesvarte: number;
  bekreftPaNytt: number;
  motsigelser: { modulId: string; id: string; tekst: string; sporsmalIder: string[] }[];
}

export interface LaastVersjon {
  versjon: number;
  fingeravtrykk: string;
  innholdVersjon: string;
  bekreftet: string | null;
  bekreftetAv: string | null;
}

export interface SammendragVisning {
  dokument: Sammendrag;
  fingeravtrykk: string;
  tilstand: 'aktiv' | 'bekreftet' | 'trukket_tilbake';
  kanBekreftes: boolean;
  hindringer: string[];
  laast: LaastVersjon | null;
  endretSidenLaast: boolean;
}

export interface Tolkning {
  status: 'forslag' | 'avvik';
  verdi: Svarverdi;
  valgte: Valg[];
  kundensOrd: string | null;
  sikkerhet: string;
  gjentakelse: string | null;
  oppfolging: { tekst: string; alternativer: string[] }[];
  avvik: string | null;
}

export interface TolkInn {
  sporsmalId: string;
  instans: string;
  verdi: Svarverdi;
  kundensOrd: string;
  oppfolging?: { sporsmal: string; svar: string }[];
}

const lenke = (token: string) => `/api/reise/lenke/${encodeURIComponent(token)}`;

export const reiseApi = {
  lenkestatus: (token: string) => api<Lenkestatus>(lenke(token)),
  sendKode: (token: string) => api<void>(`${lenke(token)}/send-kode`, 'POST'),
  bekreft: (token: string, kode: string) => api<{ status: string; reiseId: string }>(`${lenke(token)}/bekreft`, 'POST', { kode }),
  /** Med `modul` er det det første ubesvarte spørsmålet i modulen kunden har valgt (T104). */
  neste: (modul?: string | null) => api<Neste>(modul ? `/api/reise/neste?modul=${encodeURIComponent(modul)}` : '/api/reise/neste'),
  svar: (inn: SvarInn) => api<SvarResultat>('/api/reise/svar', 'POST', inn),
  tilbake: (fra: { sporsmalId: string; instans: string } | null) => api<Sporsmal>('/api/reise/tilbake', 'POST', fra ?? {}),
  fremdrift: () => api<Fremdrift>('/api/reise/fremdrift'),
  sporsmal: (id: string, instans: string) =>
    api<Sporsmal>(`/api/reise/sporsmal/${encodeURIComponent(id)}?instans=${encodeURIComponent(instans)}`),
  settSprak: (sprak: Sprak) => api<void>('/api/reise/sprak', 'PUT', { sprak }),
  svarListe: () => api<GjeldendeSvar[]>('/api/reise/svar'),
  meg: () => api<Meg>('/api/reise/meg'),
  deltakere: () => api<Deltaker[]>('/api/reise/deltakere'),
  inviter: (kollega: NyKollega) => api<{ deltakerId: string }>('/api/reise/deltakere', 'POST', kollega),
  loggUt: () => api<void>('/api/reise/loggut', 'POST'),
  tolk: (inn: TolkInn) => api<Tolkning>('/api/reise/tolk', 'POST', inn),
  bekreftTolkning: (sporsmalId: string, instans: string) => api<SvarResultat>('/api/reise/tolk/bekreft', 'POST', { sporsmalId, instans }),
  sammendrag: () => api<SammendragVisning>('/api/reise/sammendrag'),
  bekreftSammendrag: (fingeravtrykk: string) => api<LaastVersjon>('/api/reise/sammendrag/bekreft', 'POST', { fingeravtrykk }),
  endreEtterBekreftelse: () => api<void>('/api/reise/sammendrag/endre', 'POST'),
};
